/**
 * Daily Costs Bar Chart
 *
 * Displays daily cost trends with stacked bars for different cost types
 * Uses Recharts library for visualizations
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import type { ProjectDailyCost } from '../../api/projects';

export interface DailyCostsBarChartProps {
  data: ProjectDailyCost[];
  isLoading?: boolean;
}

const COLORS = {
  Agent_Cost: '#3b82f6',    // blue
  API_Cost: '#10b981',      // green
  Storage_Cost: '#f59e0b',  // amber
  Compute_Cost: '#8b5cf6',  // purple
  Other_Cost: '#6b7280'     // gray
};

export const DailyCostsBarChart: React.FC<DailyCostsBarChartProps> = ({
  data,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="animate-pulse text-gray-400">Loading chart data...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="text-center">
          <p className="text-gray-500">No cost history available</p>
          <p className="text-sm text-gray-400 mt-1">Daily costs will appear as they are incurred</p>
        </div>
      </div>
    );
  }

  // Transform data for chart (reverse to show oldest to newest)
  const chartData = [...data].reverse().map(item => ({
    date: new Date(item.Cost_Date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    fullDate: item.Cost_Date,
    Agent_Cost: parseFloat(item.Agent_Cost.toString()),
    API_Cost: parseFloat(item.API_Cost.toString()),
    Storage_Cost: parseFloat(item.Storage_Cost.toString()),
    Compute_Cost: parseFloat(item.Compute_Cost.toString()),
    Other_Cost: parseFloat(item.Other_Cost.toString()),
    Daily_Total: parseFloat(item.Daily_Total.toString()),
    Event_Count: item.Event_Count
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, item: any) => sum + item.value, 0);
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          <p className="text-sm font-medium text-gray-700 mb-1">
            Total: ${total.toFixed(2)}
          </p>
          {payload.map((item: any, index: number) => (
            item.value > 0 && (
              <p key={index} className="text-sm text-gray-600" style={{ color: item.color }}>
                {item.name.replace('_', ' ')}: ${item.value.toFixed(2)}
              </p>
            )
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 12, fill: '#6b7280' }}
          stroke="#9ca3af"
        />
        <YAxis
          tick={{ fontSize: 12, fill: '#6b7280' }}
          stroke="#9ca3af"
          label={{ value: 'Cost ($)', angle: -90, position: 'insideLeft', style: { fill: '#6b7280' } }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          verticalAlign="top"
          height={36}
          formatter={(value: string) => (
            <span className="text-sm text-gray-700">{value.replace('_', ' ')}</span>
          )}
        />
        <Bar dataKey="Agent_Cost" stackId="costs" fill={COLORS.Agent_Cost} />
        <Bar dataKey="API_Cost" stackId="costs" fill={COLORS.API_Cost} />
        <Bar dataKey="Storage_Cost" stackId="costs" fill={COLORS.Storage_Cost} />
        <Bar dataKey="Compute_Cost" stackId="costs" fill={COLORS.Compute_Cost} />
        <Bar dataKey="Other_Cost" stackId="costs" fill={COLORS.Other_Cost} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default DailyCostsBarChart;
